import { useEffect, useState } from 'react';
import { useLocation, useHistory, Redirect } from 'react-router-dom';
import styled from '@emotion/styled';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { setStep, setSchoolName } from '../rcSlice';
import RcLayout from '../components/layouts/RcLayout';
import ContractDatesStep from '../steps/ContractDatesStep';
import PersonalInfoStep from '../steps/PersonalInfoStep';
import RecapAndPaymentStep from '../steps/RecapAndPaymentStep';
import { isAuthenticated } from '@/services/utils';
import { getMe } from '@/api/authApi';
import LocalStorageService from '@/services/LocalStorageService';

const Spacing = styled.div`
  height: 50px;
  @media (max-width: 600px) {
    height: 10px;
  }
`;

const RcPage = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const history = useHistory();

  const step = useAppSelector(state => state.rc.step);
  const lastVerifiedStep = useAppSelector(state => state.rc.lastVerifiedStep);
  const [isLogged, setIsLogged] = useState<boolean>(!!isAuthenticated());
  const [authChecked, setAuthChecked] = useState(false);
  const isTempRegistered = useAppSelector(state => state.user.isTempRegistered);

  useEffect(() => {
    let cancelled = false;
    const verifyAuth = async () => {
      const localUser = isAuthenticated();
      if (!localUser) {
        if (!cancelled) {
          setIsLogged(false);
          setAuthChecked(true);
        }
        return;
      }
      try {
        await getMe();
        if (!cancelled) {
          setIsLogged(true);
          setAuthChecked(true);
        }
      } catch {
        LocalStorageService.clearUser();
        LocalStorageService.clearToken();
        if (!cancelled) {
          setIsLogged(false);
          setAuthChecked(true);
        }
      }
    };
    verifyAuth();
    return () => { cancelled = true; };
  }, []);

  // Sync URL → Redux on initial load / navigation
  useEffect(() => {
    const params = new URLSearchParams(location.search);

    // Capture ?school=... à l'arrivée pour rattacher l'inscription temporaire
    // à la bonne école si l'utilisateur n'est pas encore connecté.
    const schoolParam = params.get('school');
    if (schoolParam) {
      dispatch(setSchoolName(schoolParam.trim().toLowerCase()));
    }

    const stepParam = parseInt(params.get('step') || '1', 10);
    const desiredStep = isNaN(stepParam) ? 1 : stepParam;

    if (desiredStep > lastVerifiedStep + 1) {
      params.set('step', `${lastVerifiedStep + 1}`);
      history.replace({ search: params.toString() });
      return;
    }

    if (desiredStep < 1) {
      params.set('step', '1');
      history.replace({ search: params.toString() });
      return;
    }

    if (step !== desiredStep && desiredStep !== 0) {
      dispatch(setStep(desiredStep));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Sync Redux → URL when step changes from within components
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlStep = parseInt(params.get('step') || '1', 10);

    if (urlStep !== step) {
      params.set('step', step.toString());
      history.replace({ search: params.toString() });
    }
  }, [step, location.search, history]);

  const renderStep = () => {
    switch (step) {
      case 1:
        return <ContractDatesStep />;
      case 2:
        if (!authChecked) return null;
        return isLogged || isTempRegistered ? <RecapAndPaymentStep /> : <PersonalInfoStep />;
      case 3:
        if (!authChecked) return null;
        return isLogged || isTempRegistered ? <RecapAndPaymentStep /> : <Redirect to="/insurance/rc?step=1" />;
      default:
        return <Redirect to="/insurance/rc?step=1" />;
    }
  };

  return (
    <RcLayout>
      <Spacing />
      {renderStep()}
    </RcLayout>
  );
};

export default RcPage;
